import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from "react";
import {
  getTMLogs,
  getTMLogById,
  getTMPriceByReport,
} from "../../../services/api/tmlog";
import { getUsersByRole } from "../../../services/api/core";

/**
 * Context for T&M Log operations
 * Manages the list of reports and prices, providing centralized data access
 * and eliminating redundant API calls across TMLog, TMReport, and TMPrice pages
 *
 * @context TMLogContext
 *
 * @typedef {Object} TMLogContextType
 * @property {Array} reports - List of all T&M reports
 * @property {boolean} loading - Whether data is currently being loaded
 * @property {Function} refreshReports - Refresh the reports list from the API
 * @property {Function} reportNewItem - (id) => Promise<void>: silent refresh + brief highlight (call after create/clone)
 * @property {number|null} highlightAddedItemId - ID of the row/card to highlight (for TicketLogTable)
 * @property {number|null} highlightDeletedItemId - ID of the row/card to show red delete flash
 * @property {Function} executeDelete - (item, deleteApiFn) => Promise<void>: red flash, delete, silent refresh (for TicketLogTable)
 * @property {Function} getReportById - Get a report by ID (from cache or API)
 * @property {Function} getPriceByReport - Get price data for a report (from cache or API)
 * @property {Array} workers - List of active users (workers)
 * @property {boolean} workersLoading - Whether workers data is currently being loaded
 */
export const TMLogContext = createContext(null);

/**
 * Read TMLogContext. Throws if used outside the T&M provider.
 *
 * @returns {Object}
 */
/**
 * Reads TMLogContext. Throws if used outside a T&M provider.
 *
 * @returns {Object} T&M log context value.
 */
export function useTMLogContext() {
  const context = useContext(TMLogContext);
  if (!context) {
    throw new Error("useTMLogContext must be used within a TMLogProvider");
  }
  return context;
}

const HIGHLIGHT_DURATION_MS = 2000;

/**
 * Build T&M log context: report list, caches, workers, and highlight helpers.
 *
 * @returns {Object} Value passed to `TMLogContext.Provider`.
 */
export function useTMLogCtx() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportCache, setReportCache] = useState({});
  const [priceCache, setPriceCache] = useState({});
  const [workers, setWorkers] = useState([]);
  const [workersLoading, setWorkersLoading] = useState(true);
  const [highlightAddedItemId, setHighlightAddedItemId] = useState(null);
  const [highlightDeletedItemId, setHighlightDeletedItemId] = useState(null);

  // Fetch all reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTMLogs();
      setReports(data);
      return data;
    } catch (error) {
      console.error("Error fetching T&M reports:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reports on mount
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Fetch workers (all active users) on mount
  useEffect(() => {
    /**
     * Loads active users for T&M worker pickers.
     *
     * @returns {Promise<void>}
     */
    const fetchWorkers = async () => {
      try {
        setWorkersLoading(true);
        const data = await getUsersByRole({ active: true });
        setWorkers(data || []);
      } catch (error) {
        console.error("Error fetching workers:", error);
        setWorkers([]);
      } finally {
        setWorkersLoading(false);
      }
    };
    fetchWorkers();
  }, []);

  // Refresh reports (call after mutations).
  // Options: { silent: true } refetches without setting loading state (better UX for clone/quick updates).
  const refreshReports = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);
      const data = await getTMLogs();
      setReports(data);
      setReportCache({});
      setPriceCache({});
      return data;
    } catch (error) {
      console.error("Error refreshing T&M reports:", error);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Report a newly added report: silent refresh + brief highlight flash on the list.
  // Call after create or clone so the list updates and the new row/card is highlighted.
  const reportNewItem = useCallback(async (id) => {
    if (id == null) return;
    await refreshReports({ silent: true });
    setHighlightAddedItemId(id);
    setTimeout(() => setHighlightAddedItemId(null), HIGHLIGHT_DURATION_MS);
  }, [refreshReports]);

  // Confirm delete flow: flash row red, call delete API, wait for animation, then silent refresh.
  const executeDelete = useCallback(async (item, deleteApiFn) => {
    if (!item?.id || typeof deleteApiFn !== "function") return;
    setHighlightDeletedItemId(item.id);
    const deleteFlashMs = 1000;
    try {
      await deleteApiFn(item);
      await new Promise((r) => setTimeout(r, deleteFlashMs));
      await refreshReports({ silent: true });
    } finally {
      setTimeout(() => setHighlightDeletedItemId(null), deleteFlashMs);
    }
  }, [refreshReports]);

  // Get report by ID - use cache or fetch
  const getReportById = useCallback(
    async (id) => {
      // Check cache first
      if (reportCache[id]) {
        return reportCache[id];
      }

      // Check if it's in the reports list
      const reportInList = reports.find((r) => r.id === parseInt(id));
      if (reportInList) {
        setReportCache((prev) => ({ ...prev, [id]: reportInList }));
        return reportInList;
      }

      // Fetch from API
      try {
        const data = await getTMLogById(id);
        setReportCache((prev) => ({ ...prev, [id]: data }));
        return data;
      } catch (error) {
        console.error(`Error fetching report ${id}:`, error);
        throw error;
      }
    },
    [reports, reportCache]
  );

  // Get price by report ID - use cache or fetch
  const getPriceByReport = useCallback(
    async (reportId) => {
      // Check cache first
      if (priceCache[reportId]) {
        return priceCache[reportId];
      }

      // Check if the report in list has pricing data
      const reportInList = reports.find((r) => r.id === parseInt(reportId));
      if (reportInList?.pricing) {
        setPriceCache((prev) => ({ ...prev, [reportId]: reportInList.pricing }));
        return reportInList.pricing;
      }

      // Fetch from API (returns null when no price exists yet - expected for new reports)
      const data = await getTMPriceByReport(reportId);
      setPriceCache((prev) => ({ ...prev, [reportId]: data ?? null }));
      return data ?? null;
    },
    [reports, priceCache]
  );

  const contextValue = useMemo(
    () => ({
      reports,
      loading,
      refreshReports,
      reportNewItem,
      highlightAddedItemId,
      highlightDeletedItemId,
      executeDelete,
      getReportById,
      getPriceByReport,
      workers,
      workersLoading,
    }),
    [reports, loading, refreshReports, reportNewItem, highlightAddedItemId, highlightDeletedItemId, executeDelete, getReportById, getPriceByReport, workers, workersLoading]
  );

  return contextValue;
}

export default useTMLogCtx; // Export the hook as default for backward compatibility
